
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Datenschutzerklärung - Jo Allround Service';
    
    // Schema.org JSON-LD für Datenschutzerklärung
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Datenschutzerklärung - Jo Allround Service",
      "description": "Datenschutzerklärung von Jo Allround Service - Informationen zum Umgang mit personenbezogenen Daten gemäß DSGVO",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://jo-allround.de'}/privacy`,
      "dateModified": "2025-01-01",
      "mainEntity": {
        "@type": "PrivacyPolicy",
        "name": "Datenschutzerklärung",
        "dateModified": "2025-01-01",
        "publisher": {
          "@type": "LocalBusiness",
          "name": "Jo Allround Service",
          "owner": {
            "@type": "Person",
            "name": "Joachim Karl Born"
          }
        }
      }
    });
    
    // Entferne vorhandenes Schema falls vorhanden
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-page="privacy"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    schemaScript.setAttribute('data-page', 'privacy');
    document.head.appendChild(schemaScript);
    
    return () => {
      const script = document.querySelector('script[type="application/ld+json"][data-page="privacy"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Datenschutzerklärung</h1>
          
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Letzte Aktualisierung:</strong> 01. Januar 2025<br />
                Diese Datenschutzerklärung entspricht den Anforderungen der EU-Datenschutz-Grundverordnung (DSGVO) und dem Bundesdatenschutzgesetz (BDSG).
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">1. Verantwortlicher</h2>
            <p className="text-gray-600 mb-4">
              Verantwortlicher im Sinne der Datenschutz-Grundverordnung und anderer nationaler Datenschutzgesetze der Mitgliedsstaaten sowie sonstiger datenschutzrechtlicher Bestimmungen ist:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-6">
              <p className="text-gray-700">
                <strong>Jo Allround Service</strong><br />
                Inhaber: Joachim Karl Born<br />
                Rheinsberg, OPR<br />
                Deutschland<br />
                <br />
                Telefon: 03943 / 62 66 73<br />
                Mobil: 0175 / 7 44 30 33<br />
                E-Mail: J-Born@T-Online.de
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">2. Überblick über die Datenverarbeitung</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">2.1 Art der verarbeiteten Daten</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
              <li>Bestandsdaten (z.B. Namen, Adressen)</li>
              <li>Kontaktdaten (z.B. E-Mail, Telefonnummern)</li>
              <li>Inhaltsdaten (z.B. Texteingaben, Fotografien, Videos)</li>
              <li>Nutzungsdaten (z.B. besuchte Webseiten, Interesse an Inhalten, Zugriffszeiten)</li>
              <li>Meta-/Kommunikationsdaten (z.B. Geräte-Informationen, IP-Adressen)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">2.2 Kategorien betroffener Personen</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
              <li>Webseitenbesucher</li>
              <li>Interessenten</li>
              <li>Kunden</li>
              <li>Kommunikationspartner</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">2.3 Zwecke der Verarbeitung</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
              <li>Erbringung vertraglicher Leistungen und Kundenservice</li>
              <li>Kontaktanfragen und Kommunikation</li>
              <li>Sicherheitsmaßnahmen</li>
              <li>Reichweitenmessung/Marketing</li>
              <li>Verwaltung und Beantwortung von Anfragen</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">3. Maßgebliche Rechtsgrundlagen</h2>
            <p className="text-gray-600 mb-4">
              Nach Maßgabe des Art. 13 DSGVO teilen wir Ihnen die Rechtsgrundlagen unserer Datenverarbeitungen mit:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
              <li><strong>Einwilligung (Art. 6 Abs. 1 S. 1 lit. a. DSGVO)</strong> - Sofern wir Ihre Einwilligung für die Verarbeitung von Daten einholen</li>
              <li><strong>Vertragserfüllung (Art. 6 Abs. 1 S. 1 lit. b. DSGVO)</strong> - Verarbeitung zur Erfüllung unserer Verträge und vorvertraglicher Maßnahmen</li>
              <li><strong>Rechtliche Verpflichtung (Art. 6 Abs. 1 S. 1 lit. c. DSGVO)</strong> - Verarbeitung zur Erfüllung rechtlicher Verpflichtungen</li>
              <li><strong>Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f. DSGVO)</strong> - Verarbeitung zur Wahrung unserer berechtigten Interessen</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">4. Sicherheitsmaßnahmen</h2>
            <p className="text-gray-600 mb-4">
              Wir treffen nach Maßgabe des Art. 32 DSGVO unter Berücksichtigung des Stands der Technik, der Implementierungskosten und der Art, des Umfangs, der Umstände und der Zwecke der Verarbeitung sowie der unterschiedlichen Eintrittswahrscheinlichkeit und Schwere des Risikos für die Rechte und Freiheiten natürlicher Personen, geeignete technische und organisatorische Maßnahmen, um ein dem Risiko angemessenes Schutzniveau zu gewährleisten.
            </p>
            <p className="text-gray-600 mb-4">
              Zu den Maßnahmen gehören insbesondere die Sicherung der Vertraulichkeit, Integrität und Verfügbarkeit von Daten durch Kontrolle des physischen Zugangs zu den Daten, als auch des sie betreffenden Zugriffs, der Eingabe, Weitergabe, der Sicherung der Verfügbarkeit und ihrer Trennung. Des Weiteren haben wir Verfahren eingerichtet, die eine Wahrnehmung von Betroffenenrechten, Löschung von Daten und Reaktion auf Gefährdung der Daten gewährleisten.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">5. Übermittlung von personenbezogenen Daten</h2>
            <p className="text-gray-600 mb-4">
              Im Rahmen unserer Datenverarbeitung kann es vorkommen, dass die Daten an andere Stellen, Unternehmen, rechtlich selbstständige Organisationseinheiten oder Personen übermittelt oder sie ihnen gegenüber offengelegt werden. Zu den Empfängern dieser Daten können z.B. mit IT-Aufgaben beauftragte Dienstleister oder Anbieter von Diensten und Inhalten, die in eine Webseite eingebunden werden, gehören.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">6. Datenverarbeitung in Drittländern</h2>
            <p className="text-gray-600 mb-4">
              Sofern wir Daten in einem Drittland (d.h., außerhalb der Europäischen Union (EU) oder des Europäischen Wirtschaftsraums (EWR)) verarbeiten oder die Verarbeitung im Rahmen der Inanspruchnahme von Diensten Dritter oder der Offenlegung bzw. Übermittlung von Daten an andere Personen, Stellen oder Unternehmen stattfindet, erfolgt dies nur im Einklang mit den gesetzlichen Vorgaben.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">7. Löschung von Daten</h2>
            <p className="text-gray-600 mb-4">
              Die von uns verarbeiteten Daten werden nach Maßgabe der gesetzlichen Vorgaben gelöscht, sobald deren zur Verarbeitung erlaubten Einwilligungen widerrufen werden oder sonstige Erlaubnisse entfallen (z.B. wenn der Zweck der Verarbeitung dieser Daten entfallen ist).
            </p>
            <p className="text-gray-600 mb-4">
              Sofern die Daten nicht gelöscht werden, weil sie für andere und gesetzlich zulässige Zwecke erforderlich sind, wird deren Verarbeitung auf diese Zwecke beschränkt. D.h., die Daten werden gesperrt und nicht für andere Zwecke verarbeitet.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">8. Bereitstellung des Onlineangebotes und Webhosting</h2>
            <p className="text-gray-600 mb-4">
              Um unser Onlineangebot sicher und effizient bereitstellen zu können, nehmen wir die Leistungen von einem oder mehreren Webhosting-Anbietern in Anspruch, von deren Servern (bzw. von ihnen verwalteten Servern) das Onlineangebot abgerufen werden kann.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Verarbeitete Datenarten:</strong> Inhaltsdaten, Nutzungsdaten, Meta-/Kommunikationsdaten.<br />
              <strong>Betroffene Personen:</strong> Nutzer.<br />
              <strong>Zwecke der Verarbeitung:</strong> Bereitstellung unseres Onlineangebotes und Nutzerfreundlichkeit.<br />
              <strong>Rechtsgrundlage:</strong> Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f. DSGVO).
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">9. Erhebung von Zugriffsdaten und Logfiles</h2>
            <p className="text-gray-600 mb-4">
              Wir selbst (bzw. unser Webhostinganbieter) erheben Daten zu jedem Zugriff auf den Server (sogenannte Serverlogfiles). Zu den Serverlogfiles können die Adresse und Name der abgerufenen Webseiten und Dateien, Datum und Uhrzeit des Abrufs, übertragene Datenmengen, Meldung über erfolgreichen Abruf, Browsertyp nebst Version, das Betriebssystem des Nutzers, Referrer URL und die anfragende IP-Adresse gehören.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Verarbeitete Datenarten:</strong> Inhaltsdaten, Nutzungsdaten, Meta-/Kommunikationsdaten.<br />
              <strong>Betroffene Personen:</strong> Nutzer.<br />
              <strong>Zwecke der Verarbeitung:</strong> Sicherheitsmaßnahmen, Verwaltung und Beantwortung von Anfragen.<br />
              <strong>Rechtsgrundlage:</strong> Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f. DSGVO).<br />
              <strong>Löschung der Daten:</strong> Logfile-Informationen werden für die Dauer von maximal 30 Tagen gespeichert und danach gelöscht oder anonymisiert.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">10. Cookies und Reichweitenmessung</h2>
            <p className="text-gray-600 mb-4">
              Cookies sind kleine Textdateien, bzw. sonstige Speichervermerke, die Informationen auf Endgeräten speichern und Informationen aus den Endgeräten auslesen. Z.B. um den Login-Status in einem Nutzerkonto, einen Warenkorbinhalt in einem E-Shop, die aufgerufenen Inhalte oder verwendete Funktionen eines Onlineangebotes speichern.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">10.1 Cookie-Kategorien</h3>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Notwendige Cookies</h4>
                <p className="text-sm text-gray-600">
                  Diese Cookies sind für das ordnungsgemäße Funktionieren der Website erforderlich und können nicht deaktiviert werden. Sie speichern keine persönlich identifizierbaren Informationen.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Rechtsgrundlage:</strong> Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f. DSGVO)
                </p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Analyse-Cookies</h4>
                <p className="text-sm text-gray-600">
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Rechtsgrundlage:</strong> Einwilligung (Art. 6 Abs. 1 S. 1 lit. a. DSGVO)
                </p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Marketing-Cookies</h4>
                <p className="text-sm text-gray-600">
                  Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen und die Effektivität von Werbekampagnen zu messen.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Rechtsgrundlage:</strong> Einwilligung (Art. 6 Abs. 1 S. 1 lit. a. DSGVO)
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">10.2 Widerspruch und Löschung</h3>
            <p className="text-gray-600 mb-4">
              Sie können Ihre Einwilligung jederzeit widerrufen oder der Verarbeitung Ihrer Daten durch Cookies widersprechen. Dies können Sie über die Cookie-Einstellungen Ihres Browsers oder über unser Cookie-Banner tun.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">11. Kontaktaufnahme</h2>
            <p className="text-gray-600 mb-4">
              Bei der Kontaktaufnahme mit uns (z.B. per Kontaktformular, E-Mail, Telefon oder via soziale Medien) werden die Angaben des Anfragenden verarbeitet, soweit dies zur Beantwortung der Kontaktanfragen und etwaiger angeschlossener Maßnahmen erforderlich ist.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Verarbeitete Datenarten:</strong> Bestandsdaten, Kontaktdaten, Inhaltsdaten.<br />
              <strong>Betroffene Personen:</strong> Kommunikationspartner.<br />
              <strong>Zwecke der Verarbeitung:</strong> Kontaktanfragen und Kommunikation.<br />
              <strong>Rechtsgrundlage:</strong> Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f. DSGVO), Vertragserfüllung und vorvertragliche Anfragen (Art. 6 Abs. 1 S. 1 lit. b. DSGVO).<br />
              <strong>Löschung der Daten:</strong> Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich sind. Dies ist dann der Fall, wenn die jeweilige Konversation mit dem Nutzer beendet ist.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">12. Google Maps</h2>
            <p className="text-gray-600 mb-4">
              Wir nutzen auf unserer Website Google Maps zur Darstellung unseres Standorts. Google Maps ist ein Kartendienst der Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA ("Google").
            </p>
            <p className="text-gray-600 mb-4">
              Durch die Nutzung von Google Maps können Informationen über Ihre Benutzung dieser Website (einschließlich Ihrer IP-Adresse) an einen Server von Google in den USA übertragen und dort gespeichert werden. Google wird diese Informationen benutzen, um Ihre Nutzung der Website auszuwerten, um Reports über die Websiteaktivitäten für uns zusammenzustellen und um weitere mit der Websitenutzung und der Internetnutzung verbundene Dienstleistungen zu erbringen.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Verarbeitete Datenarten:</strong> Nutzungsdaten, Meta-/Kommunikationsdaten.<br />
              <strong>Betroffene Personen:</strong> Nutzer.<br />
              <strong>Zwecke der Verarbeitung:</strong> Bereitstellung unseres Onlineangebotes und Nutzerfreundlichkeit, Standortdarstellung.<br />
              <strong>Rechtsgrundlage:</strong> Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f. DSGVO).<br />
              <strong>Weitere Informationen:</strong> <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Datenschutzerklärung von Google</a>
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">13. Rechte der betroffenen Personen</h2>
            <p className="text-gray-600 mb-4">
              Ihnen stehen als Betroffene nach der DSGVO verschiedene Rechte zu, die sich insbesondere aus Art. 15 bis 21 DSGVO ergeben:
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Widerspruchsrecht (Art. 21 DSGVO)</h4>
                <p className="text-sm text-gray-600">
                  Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung der Sie betreffenden personenbezogenen Daten, die aufgrund von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt, Widerspruch einzulegen.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Widerrufsrecht bei Einwilligungen</h4>
                <p className="text-sm text-gray-600">
                  Sie haben das Recht, erteilte Einwilligungen gem. Art. 7 Abs. 3 DSGVO mit Wirkung für die Zukunft zu widerrufen.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Auskunftsrecht (Art. 15 DSGVO)</h4>
                <p className="text-sm text-gray-600">
                  Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob betreffende Daten verarbeitet werden und auf Auskunft über diese Daten sowie auf weitere Informationen und Kopie der Daten entsprechend den gesetzlichen Vorgaben.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Berichtigungsrecht (Art. 16 DSGVO)</h4>
                <p className="text-sm text-gray-600">
                  Sie haben entsprechend den gesetzlichen Vorgaben das Recht, die Vervollständigung der Sie betreffenden Daten oder die Berichtigung der Sie betreffenden unrichtigen Daten zu verlangen.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Löschungsrecht und Einschränkungsrecht (Art. 17 und 18 DSGVO)</h4>
                <p className="text-sm text-gray-600">
                  Sie haben das Recht, entsprechend den gesetzlichen Vorgaben zu verlangen, dass Sie betreffende Daten unverzüglich gelöscht werden, bzw. alternativ nach Maßgabe der gesetzlichen Vorgaben eine Einschränkung der Verarbeitung der Daten zu verlangen.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Datenübertragbarkeitsrecht (Art. 20 DSGVO)</h4>
                <p className="text-sm text-gray-600">
                  Sie haben das Recht, Sie betreffende Daten, die Sie uns bereitgestellt haben, entsprechend den gesetzlichen Vorgaben in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten oder deren Übermittlung an einen anderen Verantwortlichen zu fordern.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Beschwerde bei Aufsichtsbehörde (Art. 77 DSGVO)</h4>
                <p className="text-sm text-gray-600">
                  Sie haben unbeschadet eines anderweitigen verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs das Recht auf Beschwerde bei einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthaltsorts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes, wenn Sie der Ansicht sind, dass die Verarbeitung der Sie betreffenden personenbezogenen Daten gegen die DSGVO verstößt.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">13. Begriffsdefinitionen</h2>
            <p className="text-gray-600 mb-4">
              In diesem Abschnitt erhalten Sie eine Übersicht über die in dieser Datenschutzerklärung verwendeten Begrifflichkeiten. Viele der Begriffe sind dem Gesetz entnommen und vor allem im Art. 4 DSGVO definiert.
            </p>
            
            <div className="space-y-3">
              <div>
                <strong className="text-gray-800">Personenbezogene Daten:</strong>
                <span className="text-gray-600"> "Personenbezogene Daten" sind alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen.</span>
              </div>
              <div>
                <strong className="text-gray-800">Verantwortlicher:</strong>
                <span className="text-gray-600"> Als "Verantwortlicher" wird die natürliche oder juristische Person, Behörde, Einrichtung oder andere Stelle, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten entscheidet, bezeichnet.</span>
              </div>
              <div>
                <strong className="text-gray-800">Verarbeitung:</strong>
                <span className="text-gray-600"> "Verarbeitung" ist jeder mit oder ohne Hilfe automatisierter Verfahren ausgeführte Vorgang oder jede solche Vorgangsreihe im Zusammenhang mit personenbezogenen Daten.</span>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">14. Änderungen der Datenschutzerklärung</h2>
            <p className="text-gray-600 mb-4">
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen, z.B. bei der Einführung neuer Services. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">15. Fragen zum Datenschutz</h2>
            <p className="text-gray-600 mb-4">
              Wenn Sie Fragen zum Datenschutz haben, schreiben Sie uns bitte eine E-Mail oder wenden Sie sich direkt an die für den Datenschutz verantwortliche Person in unserer Organisation:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="text-gray-700">
                <strong>Joachim Karl Born</strong><br />
                E-Mail: J-Born@T-Online.de<br />
                Telefon: 03943 / 62 66 73
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-8">
              <p className="text-sm text-gray-600">
                <strong>Stand:</strong> 01. Januar 2025<br />
                Diese Datenschutzerklärung wurde erstellt durch Anpassung an die aktuellen Bestimmungen der DSGVO und des BDSG.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
